import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import useDisableScrolling from '../../Hooks/useDisableScrolling';

export function Modal({ children, expand = false, timeout = false, disableScrolling = true, onBackdropClick }) {
  const [isFinished, setIsFinished] = useState(!timeout);
  const overlayRef = useRef(null);
  const isActive = disableScrolling && (expand || !isFinished);

  useEffect(() => {
    if (timeout) setIsFinished(false);
    const t = setTimeout(() => setIsFinished(true), 800);
    return () => clearTimeout(t);
  }, [expand, timeout]);

  // Stop Lenis + lock native overflow
  useDisableScrolling(isActive);

  // Capture wheel/touch at the overlay level so Lenis never sees them.
  // If the event target has a scrollable ancestor inside the overlay, let it
  // scroll naturally. Otherwise swallow the event entirely.
  useEffect(() => {
    const el = overlayRef.current;
    if (!el || !isActive) return;

    const block = (e) => {
      // Always stop propagation first, Lenis listens on window in bubble phase,
      // so cutting the chain here ensures it never receives the event regardless
      // of whether we allow or block the actual scroll.
      e.stopPropagation();

      // Walk up from the target to find a scrollable ancestor inside the overlay.
      // If found: don't call preventDefault, let the browser scroll it natively.
      // If not found: call preventDefault, nothing should scroll.
      let node = e.target;
      while (node) {
        const { overflow, overflowY } = window.getComputedStyle(node);
        if (/auto|scroll/.test(overflow + overflowY) && node.scrollHeight > node.clientHeight) {
          return; // scrollable child found, native scroll allowed
        }
        if (node === el) break;
        node = node.parentElement;
      }
      e.preventDefault();
    };

    el.addEventListener('wheel', block, { passive: false });
    el.addEventListener('touchmove', block, { passive: false });
    return () => {
      el.removeEventListener('wheel', block);
      el.removeEventListener('touchmove', block);
    };
  }, [isActive]);

  const visible = expand || !isFinished;

  if (typeof document === 'undefined') return null;

  return ReactDOM.createPortal(
    <div
      ref={overlayRef}
      style={{ position: 'fixed', inset: 0, zIndex: 100, backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto', overscrollBehavior: 'contain' }}
      className={`transition-opacity font-primary duration-500 ease-in-out ${visible ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
    >
      <div
        className="min-h-full w-full flex items-center justify-center p-4 md:p-10"
        onClick={(e) => { if (e.target === e.currentTarget && onBackdropClick) onBackdropClick(); }}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}
